import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Badge } from './ui/badge';
import { Bell, Clock, User, X } from 'lucide-react';
import { checkUpcomingAppointments, getTodayAppointments, formatTimeRemaining } from '../utils/notifications';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const NotificationBell = ({ appointments }) => {
  const [notifications, setNotifications] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    updateNotifications();
    // Actualizar cada minuto
    const interval = setInterval(updateNotifications, 60000);
    return () => clearInterval(interval);
  }, [appointments]);

  const updateNotifications = () => {
    const upcoming = checkUpcomingAppointments(appointments);
    const today = getTodayAppointments(appointments);
    
    setNotifications(upcoming);
    setTodayCount(today.length);
  };

  const dismissNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const notificationCount = notifications.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" className="relative hover:bg-blue-50">
          <Bell className="w-5 h-5 text-gray-700" />
          {notificationCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
              {notificationCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-green-500 text-white pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Notificaciones</span>
              {todayCount > 0 && (
                <Badge className="bg-white text-blue-600">
                  {todayCount} cita{todayCount > 1 ? 's' : ''} hoy
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {notificationCount === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No hay citas próximas</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="p-4 border-b hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-blue-500" />
                        <span className="font-semibold text-sm">
                          {notification.patientName}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 hover:bg-red-100"
                        onClick={() => dismissNotification(notification.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-1">
                      <Clock className="w-3 h-3" />
                      <span>
                        {notification.time} - En {formatTimeRemaining(notification.minutesDiff / 60)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {notification.reason}
                    </p>
                    <div className="mt-2">
                      <Badge className="bg-orange-100 text-orange-800 border-orange-300 text-xs">
                        🔔 Cita próxima
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;